import { useEffect, useRef } from "react";
import { mediaUrl } from "@/lib/media";

interface LightboxProps {
  images: string[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  alt?: string;
}

// 이미지 라이트박스 (좌우 화살표 · 스와이프 · ESC · 키보드)
export default function Lightbox({
  images,
  index,
  onIndexChange,
  onClose,
  alt = "이미지",
}: LightboxProps) {
  const touchX = useRef<number | null>(null);
  const total = images.length;

  const prev = () => onIndexChange((index - 1 + total) % total);
  const next = () => onIndexChange((index + 1) % total);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, total]);

  if (total === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[260] flex items-center justify-center bg-primary-950/90 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${alt} 크게 보기`}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="닫기"
        className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30"
      >
        <i className="ri-close-line text-2xl" aria-hidden="true"></i>
      </button>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="이전 이미지"
            className="absolute left-3 md:left-6 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30"
          >
            <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="다음 이미지"
            className="absolute right-3 md:right-6 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30"
          >
            <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
          </button>
        </>
      )}

      <div
        className="relative flex max-h-[85vh] w-full max-w-4xl items-center justify-center"
        onTouchStart={(e) => {
          touchX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(dx) > 50) {
            if (dx < 0) next();
            else prev();
          }
          touchX.current = null;
        }}
      >
        <img
          src={mediaUrl(images[index])}
          alt={`${alt} ${index + 1}`}
          className="max-h-[85vh] w-auto max-w-full rounded-lg object-contain"
        />
      </div>

      {total > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-background-50/15 px-4 py-1.5 text-sm text-background-50">
          {index + 1} / {total}
        </div>
      )}
    </div>
  );
}